// FileUploader - Composant de téléchargement de fichiers multi-types
import React, { useState, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { 
  Upload, X, File, FileText, FileImage, FileVideo, FileAudio, 
  Loader2, CheckCircle, AlertCircle, Download, Eye, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const FILE_ICONS = {
  'image': FileImage,
  'video': FileVideo,
  'audio': FileAudio,
  'application/pdf': FileText,
  'default': File
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const getFileIcon = (mimeType) => {
  if (mimeType?.startsWith('image/')) return FILE_ICONS['image'];
  if (mimeType?.startsWith('video/')) return FILE_ICONS['video'];
  if (mimeType?.startsWith('audio/')) return FILE_ICONS['audio'];
  if (mimeType === 'application/pdf') return FILE_ICONS['application/pdf'];
  return FILE_ICONS['default'];
};

const FileUploader = ({
  value = [], // Array of file objects
  onChange,
  maxFiles = 10,
  maxFileSize = 50 * 1024 * 1024, // 50MB default, can be overridden
  acceptedTypes = '*',
  entityType = 'document', // 'case', 'complaint', 'trial', 'prisoner', 'document'
  entityId = null,
  disabled = false,
  showPreview = true,
  uploadEndpoint = '/files/upload',
  chunkSize = 5 * 1024 * 1024 // 5MB chunks for large files
}) => {
  const { language } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const handleFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList);
    
    // Validate
    if (value.length + files.length > maxFiles) {
      toast.error(
        language === 'fr' 
          ? `Maximum ${maxFiles} fichiers autorisés` 
          : `Maximum ${maxFiles} files allowed`
      );
      return;
    }

    const validFiles = files.filter(file => {
      if (file.size > maxFileSize) {
        toast.error(
          language === 'fr'
            ? `${file.name} dépasse la taille maximale (${formatFileSize(maxFileSize)})`
            : `${file.name} exceeds max size (${formatFileSize(maxFileSize)})`
        );
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    setUploading(true);

    for (const file of validFiles) {
      try {
        // Initialize progress
        setUploadProgress(prev => ({
          ...prev,
          [file.name]: { progress: 0, status: 'uploading' }
        }));

        let uploadedFile;

        // Use chunked upload for large files
        if (file.size > chunkSize) {
          uploadedFile = await uploadLargeFile(file);
        } else {
          uploadedFile = await uploadSmallFile(file);
        }

        // Update progress
        setUploadProgress(prev => ({
          ...prev,
          [file.name]: { progress: 100, status: 'complete' }
        }));

        // Add to value
        onChange?.([...value, uploadedFile]);

      } catch (error) {
        console.error('Upload error:', error);
        setUploadProgress(prev => ({
          ...prev,
          [file.name]: { progress: 0, status: 'error', error: error.message }
        }));
        toast.error(
          language === 'fr'
            ? `Erreur lors du téléchargement de ${file.name}`
            : `Error uploading ${file.name}`
        );
      }
    }

    setUploading(false);
    
    // Clear progress after delay
    setTimeout(() => setUploadProgress({}), 3000);
  }, [value, onChange, maxFiles, maxFileSize, language, entityType, entityId, chunkSize]);

  const uploadSmallFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entity_type', entityType);
    if (entityId) formData.append('entity_id', entityId);

    const response = await api.post(uploadEndpoint, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (progressEvent) => {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(prev => ({
          ...prev,
          [file.name]: { progress, status: 'uploading' }
        }));
      }
    });

    return response.data;
  };

  const uploadLargeFile = async (file) => {
    const totalChunks = Math.ceil(file.size / chunkSize);
    const uploadId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      const start = chunkIndex * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const chunk = file.slice(start, end);
      
      const formData = new FormData();
      formData.append('chunk', chunk);
      formData.append('chunk_index', chunkIndex);
      formData.append('total_chunks', totalChunks);
      formData.append('upload_id', uploadId);
      formData.append('filename', file.name);
      formData.append('file_type', file.type);
      formData.append('total_size', file.size);
      formData.append('entity_type', entityType);
      if (entityId) formData.append('entity_id', entityId);

      const response = await api.post('/files/upload-chunk', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const progress = Math.round(((chunkIndex + 1) / totalChunks) * 100);
      setUploadProgress(prev => ({
        ...prev,
        [file.name]: { progress, status: 'uploading' }
      }));

      // Return final file info on last chunk
      if (chunkIndex === totalChunks - 1) {
        return response.data;
      }
    }
  };

  const handleRemove = async (fileToRemove, index) => {
    try {
      if (fileToRemove.file_id) {
        await api.delete(`/files/${fileToRemove.file_id}`);
      }
      const newValue = value.filter((_, i) => i !== index);
      onChange?.(newValue);
      toast.success(language === 'fr' ? 'Fichier supprimé' : 'File removed');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(language === 'fr' ? 'Erreur de suppression' : 'Delete error');
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    handleFiles(e.dataTransfer.files);
  }, [handleFiles, disabled]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    if (!disabled) setDragOver(true);
  }, [disabled]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  return (
    <div className="space-y-3">
      {/* Drop zone */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => !disabled && inputRef.current?.click()}
        className={`
          border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all
          ${dragOver ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={acceptedTypes}
          onChange={(e) => handleFiles(e.target.files)}
          className="hidden"
          disabled={disabled}
        />
        
        <Upload className={`w-10 h-10 mx-auto mb-3 ${dragOver ? 'text-primary' : 'text-muted-foreground'}`} />
        
        <p className="font-medium text-sm">
          {language === 'fr' 
            ? 'Glissez vos fichiers ici ou cliquez pour parcourir'
            : 'Drag files here or click to browse'}
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          {language === 'fr'
            ? `Max ${maxFiles} fichiers, ${formatFileSize(maxFileSize)} par fichier`
            : `Max ${maxFiles} files, ${formatFileSize(maxFileSize)} per file`}
        </p>
        <div className="flex flex-wrap justify-center gap-1 mt-2">
          <Badge variant="outline" className="text-xs">PDF</Badge>
          <Badge variant="outline" className="text-xs">Images</Badge>
          <Badge variant="outline" className="text-xs">Audio</Badge>
          <Badge variant="outline" className="text-xs">Vidéo</Badge>
          <Badge variant="outline" className="text-xs">Documents</Badge>
        </div>
      </div>

      {/* Upload progress */}
      {Object.entries(uploadProgress).map(([filename, info]) => (
        <div key={filename} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
          <Loader2 className={`w-5 h-5 flex-shrink-0 ${info.status === 'uploading' ? 'animate-spin text-primary' : info.status === 'complete' ? 'text-green-500' : 'text-red-500'}`} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{filename}</p>
            <Progress value={info.progress} className="h-1 mt-1" />
          </div>
          <span className="text-xs text-muted-foreground">{info.progress}%</span>
        </div>
      ))}

      {/* Uploaded files list */}
      {value.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium">
            {language === 'fr' ? 'Fichiers joints' : 'Attached files'} ({value.length})
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {value.map((file, index) => {
              const FileIcon = getFileIcon(file.mime_type || file.type);
              return (
                <Card key={file.file_id || index} className="overflow-hidden">
                  <CardContent className="p-3">
                    <div className="flex items-start gap-3">
                      {/* Thumbnail or icon */}
                      {showPreview && file.mime_type?.startsWith('image/') && file.url ? (
                        <img 
                          src={file.url} 
                          alt={file.filename}
                          className="w-12 h-12 rounded object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded bg-muted flex items-center justify-center flex-shrink-0">
                          <FileIcon className="w-6 h-6 text-muted-foreground" />
                        </div>
                      )}
                      
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{file.filename || file.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatFileSize(file.size || file.file_size)}
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-1">
                        {file.url && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => window.open(file.url, '_blank')}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-500 hover:text-red-600"
                          onClick={() => handleRemove(file, index)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUploader;
